import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { HomepageConfig } from "@/hooks/useHomepageConfig";
import { toDriveDirectUrl } from "@/lib/utils";

interface HomepageEditorProps {
  config: HomepageConfig;
  loading: boolean;
  onSaved: () => void;
  showToast: (msg: string, type: "success" | "error") => void;
}

export default function HomepageEditor({
  config,
  loading: configLoading,
  onSaved,
  showToast,
}: HomepageEditorProps) {
  const [form, setForm] = useState<HomepageConfig>({ ...config });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setForm({ ...config });
    setHasChanges(false);
  }, [config]);

  const markChanged = () => setHasChanges(true);

  const updateField = (field: keyof HomepageConfig, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    markChanged();
  };

  const updateSlide = (index: number, value: string) => {
    const slides = [...form.hero_slides];
    slides[index] = value;
    updateField("hero_slides", slides);
  };

  const addSlide = () => {
    updateField("hero_slides", [...form.hero_slides, ""]);
  };

  const removeSlide = (index: number) => {
    const slides = form.hero_slides.filter((_, i) => i !== index);
    updateField("hero_slides", slides);
  };

  // Filtros helpers
  const updateFilterLabel = (index: number, label: string) => {
    const arr = [...form.filtros_config];
    arr[index] = { ...arr[index], label };
    updateField("filtros_config", arr);
  };

  const addFilterOption = (filterIndex: number) => {
    const arr = [...form.filtros_config];
    arr[filterIndex] = { ...arr[filterIndex], options: [...arr[filterIndex].options, ""] };
    updateField("filtros_config", arr);
  };

  const updateFilterOption = (filterIndex: number, optionIndex: number, value: string) => {
    const arr = [...form.filtros_config];
    const opts = [...arr[filterIndex].options];
    opts[optionIndex] = value;
    arr[filterIndex] = { ...arr[filterIndex], options: opts };
    updateField("filtros_config", arr);
  };

  const removeFilterOption = (filterIndex: number, optionIndex: number) => {
    const arr = [...form.filtros_config];
    arr[filterIndex] = { ...arr[filterIndex], options: arr[filterIndex].options.filter((_, i) => i !== optionIndex) };
    updateField("filtros_config", arr);
  };

  const addFilterType = () => {
    updateField("filtros_config", [...form.filtros_config, { label: "", options: [] }]);
  };

  const removeFilterType = (index: number) => {
    const arr = form.filtros_config.filter((_, i) => i !== index);
    updateField("filtros_config", arr);
  };



  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("homepage_config")
        .upsert({
          id: 1,
          hero_title: form.hero_title,
          hero_subtitle: form.hero_subtitle,
          hero_slides: form.hero_slides,
          hero_institutional_text: form.hero_institutional_text,
          hero_cta_text: form.hero_cta_text,
          hero_cta_link: form.hero_cta_link,
          que_es_title: form.que_es_title,
          que_es_image: form.que_es_image,
          que_es_banner_text: form.que_es_banner_text,
          que_es_left_text: form.que_es_left_text,
          que_es_right_text: form.que_es_right_text,
          que_es_button_text: form.que_es_button_text,
          que_es_button_link: form.que_es_button_link,
          actualidad_title: form.actualidad_title,
          territorio_title: form.territorio_title,
          voces_title: form.voces_title,
          timeline_title: form.timeline_title,
          filtros_config: form.filtros_config,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      showToast("Cambios guardados en la página de inicio", "success");
      setHasChanges(false);
      onSaved();
    } catch (err: any) {
      showToast(err.message || "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 py-12 justify-center">
        <i className="ri-loader-4-line animate-spin text-xl" />
        <span className="text-sm">Cargando configuración del inicio...</span>
      </div>
    );
  }

  const inputClass =
    "w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";
  const textareaClass =
    "w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";
  const groupLabelClass =
    "text-xs font-bold text-gray-500 uppercase tracking-wider mb-3";

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-image-line text-primary text-lg" />
          <h3 className="text-lg font-bold text-gray-900">Hero Principal</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Título principal</label>
            <input
              type="text"
              value={form.hero_title}
              onChange={(e) => updateField("hero_title", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Subtítulo</label>
            <input
              type="text"
              value={form.hero_subtitle}
              onChange={(e) => updateField("hero_subtitle", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Texto CTA</label>
            <input
              type="text"
              value={form.hero_cta_text}
              onChange={(e) => updateField("hero_cta_text", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Link CTA</label>
            <input
              type="text"
              value={form.hero_cta_link}
              onChange={(e) => updateField("hero_cta_link", e.target.value)}
              className={inputClass}
              placeholder="#actualidad o /masmed"
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Texto institucional (bajo el hero)</label>
            <textarea
              value={form.hero_institutional_text}
              onChange={(e) =>
                updateField("hero_institutional_text", e.target.value)
              }
              rows={2}
              className={textareaClass}
            />
          </div>
        </div>

        {/* Slides */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className={groupLabelClass}>Slides del carrusel</h4>
            <button
              onClick={addSlide}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
            >
              <i className="ri-add-line" />
              Agregar slide
            </button>
          </div>
          <div className="space-y-3">
            {form.hero_slides.map((slide, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 bg-gray-50 rounded-lg p-3"
              >
                <span className="text-xs font-bold text-gray-400 mt-2 w-5">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={slide}
                    onChange={(e) => updateSlide(idx, e.target.value)}
                    className={inputClass}
                    placeholder="URL de la imagen del slide"
                  />
                  {slide && (
                    <img
                      src={toDriveDirectUrl(slide)}
                      alt={`Slide ${idx + 1}`}
                      className="mt-2 w-full h-24 object-cover rounded-lg border border-gray-200"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 pt-0.5">
                  {form.hero_slides.length > 1 && (
                    <button
                      onClick={() => removeSlide(idx)}
                      className="w-9 h-9 rounded-lg border border-red-200 flex items-center justify-center text-red-400 hover:text-red-600 hover:border-red-300 transition-colors cursor-pointer"
                      title="Eliminar slide"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ¿Qué es? Section */}
      <div className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-information-line text-primary text-lg" />
          <h3 className="text-lg font-bold text-gray-900">
            Sección ¿Qué es MásMed?
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Título</label>
            <input
              type="text"
              value={form.que_es_title}
              onChange={(e) => updateField("que_es_title", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Texto de la banda naranja</label>
            <input
              type="text"
              value={form.que_es_banner_text}
              onChange={(e) => updateField("que_es_banner_text", e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Imagen principal</label>
            <div className="flex items-center gap-1.5 mb-1.5">
              <i className="ri-link text-xs text-amber-500" />
              <span className="text-[11px] text-gray-500">
                Pegar link público de Google Drive
              </span>
            </div>
            <input
              type="text"
              value={form.que_es_image}
              onChange={(e) => updateField("que_es_image", e.target.value)}
              placeholder="https://drive.google.com/file/d/.../preview"
              className={inputClass}
            />
            <p className="mt-1.5 text-[11px] text-amber-600 flex items-start gap-1">
              <i className="ri-information-line mt-px flex-shrink-0" />
              <span>El link de Drive debe ser público para que la imagen se muestre correctamente en el sitio.</span>
            </p>
            {form.que_es_image && (
              <img
                src={toDriveDirectUrl(form.que_es_image)}
                alt="Preview"
                className="mt-3 w-full h-40 object-cover rounded-lg border border-gray-200"
              />
            )}
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Texto columna izquierda</label>
            <textarea
              value={form.que_es_left_text}
              onChange={(e) => updateField("que_es_left_text", e.target.value)}
              rows={5}
              className={textareaClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Texto columna derecha</label>
            <textarea
              value={form.que_es_right_text}
              onChange={(e) => updateField("que_es_right_text", e.target.value)}
              rows={5}
              className={textareaClass}
            />
          </div>
          <div>
            <label className={labelClass}>Texto del botón</label>
            <input
              type="text"
              value={form.que_es_button_text}
              onChange={(e) => updateField("que_es_button_text", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Link del botón</label>
            <input
              type="text"
              value={form.que_es_button_link}
              onChange={(e) => updateField("que_es_button_link", e.target.value)}
              className={inputClass}
              placeholder="/masmed"
            />
          </div>
        </div>
      </div>

      {/* Títulos de secciones */}
      <div className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-heading text-primary text-lg" />
          <h3 className="text-lg font-bold text-gray-900">
            Títulos de las secciones
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Sección Actualidad</label>
            <input
              type="text"
              value={form.actualidad_title}
              onChange={(e) => updateField("actualidad_title", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Sección Territorio</label>
            <input
              type="text"
              value={form.territorio_title}
              onChange={(e) => updateField("territorio_title", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Sección Voces</label>
            <input
              type="text"
              value={form.voces_title}
              onChange={(e) => updateField("voces_title", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Sección Línea de tiempo</label>
            <input
              type="text"
              value={form.timeline_title}
              onChange={(e) => updateField("timeline_title", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Filtros de Noticias — dinámico */}
      <div className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-filter-3-line text-primary text-lg" />
            <h3 className="text-lg font-bold text-gray-900">
              Filtros de la página Noticias
            </h3>
          </div>
          <button
            onClick={addFilterType}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
          >
            <i className="ri-add-line" />
            Agregar tipo de filtro
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Crea, renombra o elimina tipos de filtro y sus opciones. Los cambios se reflejan en los dropdowns de la página de Noticias.
        </p>

        {form.filtros_config.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-lg">
            No hay filtros configurados. Usa el botón "Agregar tipo de filtro" para crear el primero.
          </p>
        )}

        <div className="space-y-5">
          {form.filtros_config.map((filtro, fi) => (
            <div key={fi} className="bg-gray-50 rounded-lg p-4">
              {/* Cabecera del tipo de filtro */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-gray-400 w-5">{fi + 1}</span>
                <input
                  type="text"
                  value={filtro.label}
                  onChange={(e) => updateFilterLabel(fi, e.target.value)}
                  className="flex-1 h-9 px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                  placeholder="Nombre del filtro (ej: Segmento, Comuna, Categoría...)"
                />
                <button
                  onClick={() => removeFilterType(fi)}
                  className="w-9 h-9 rounded-lg border border-red-200 flex items-center justify-center text-red-400 hover:text-red-600 hover:border-red-300 transition-colors cursor-pointer flex-shrink-0"
                  title="Eliminar tipo de filtro"
                >
                  <i className="ri-delete-bin-6-line" />
                </button>
              </div>

              {/* Opciones del filtro */}
              <div className="ml-8 space-y-2">
                {filtro.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => updateFilterOption(fi, oi, e.target.value)}
                      className="flex-1 h-9 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                      placeholder={`Opción ${oi + 1}`}
                    />
                    <button
                      onClick={() => removeFilterOption(fi, oi)}
                      className="w-8 h-8 rounded-lg border border-red-100 flex items-center justify-center text-red-300 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer flex-shrink-0"
                      title="Eliminar opción"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ))}
                {filtro.options.length === 0 && (
                  <p className="text-xs text-gray-400 py-1">Sin opciones. Agrega la primera.</p>
                )}
                <button
                  onClick={() => addFilterOption(fi)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
                >
                  <i className="ri-add-line" />
                  Agregar opción
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save bar */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors cursor-pointer disabled:opacity-50"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-save-line" />
              Guardar cambios
            </>
          )}
        </button>
        {hasChanges && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
            <i className="ri-edit-circle-line mr-1" />
            Sin guardar
          </span>
        )}
        {!hasChanges && (
          <span className="text-xs text-gray-400">
            <i className="ri-check-line mr-1 text-green-500" />
            Todo guardado
          </span>
        )}
      </div>
    </div>
  );
}